import React, { useEffect, useMemo, useState } from "react";
import { Check, ChevronDown, Loader2, Plus, X } from "lucide-react";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "~/components/ui/command";
import { Label } from "~/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import { cn } from "~/lib/utils";

export interface CreatableSelectOption {
  value: string;
  label: string;
}

export interface CreatableSelectProps {
  label?: string;
  options: CreatableSelectOption[];
  onCreateOption: (val: string) => Promise<CreatableSelectOption | undefined>;
  onChange: (val: CreatableSelectOption | undefined) => void;
  value?: CreatableSelectOption;
  loading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

export const CreatableSelect: React.FC<CreatableSelectProps> = (props) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [internalValue, setInternalValue] = useState<
    CreatableSelectOption | undefined
  >(props.value);
  const [isCreating, setIsCreating] = useState(false);

  const selectedOption = props.value ?? internalValue;
  const isDisabled = props.disabled || props.loading || isCreating;

  useEffect(() => {
    setInternalValue(props.value);
  }, [props.value]);

  const existingOptions = useMemo(() => props.options ?? [], [props.options]);

  const filteredOptions = useMemo(() => {
    const trimmed = search.trim().toLowerCase();
    if (!trimmed) {
      return existingOptions;
    }
    return existingOptions.filter((option) =>
      option.label.toLowerCase().includes(trimmed)
    );
  }, [existingOptions, search]);

  const handleSelect = (option: CreatableSelectOption | undefined) => {
    setInternalValue(option);
    props.onChange(option);
    setSearch("");
    setOpen(false);
  };

  const handleCreate = async (value: string) => {
    if (!value) {
      return;
    }
    try {
      setIsCreating(true);
      const result = await props.onCreateOption(value);
      if (result) {
        handleSelect(result);
      }
    } finally {
      setIsCreating(false);
      setSearch("");
    }
  };

  const canCreate = useMemo(() => {
    const trimmed = search.trim();
    if (trimmed.length === 0) {
      return false;
    }
    return !existingOptions.some(
      (option) => option.label.toLowerCase() === trimmed.toLowerCase()
    );
  }, [existingOptions, search]);

  const placeholder = props.placeholder ?? "Search and select an item";

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen || !isDisabled) {
      setOpen(nextOpen);
    }
  };

  return (
    <div className={cn("flex w-full flex-col gap-2", props.className)}>
      {props.label ? <Label>{props.label}</Label> : null}
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger>
          <button
            type="button"
            role="combobox"
            aria-expanded={open}
            disabled={isDisabled}
            data-placeholder={selectedOption ? undefined : "true"}
            className={cn(
              "border-input data-[placeholder]:text-muted-foreground [&_svg:not([class*='text-'])]:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive flex h-9 w-full items-center justify-between gap-2 rounded-md border bg-transparent px-3 py-2 text-sm shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
              isDisabled && "pointer-events-none opacity-50"
            )}
          >
            <span
              className={cn(
                "truncate text-left",
                selectedOption ? "text-foreground" : "text-muted-foreground"
              )}
            >
              {selectedOption?.label ?? placeholder}
            </span>
            <span className="flex items-center gap-2 text-muted-foreground">
              {(props.loading || isCreating) && (
                <Loader2 className="size-4 animate-spin" aria-hidden="true" />
              )}
              <ChevronDown className="size-4 opacity-50" aria-hidden="true" />
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[--radix-popover-trigger-width] p-0"
          align="start"
        >
          <Command shouldFilter={false}>
            <CommandInput
              value={search}
              onValueChange={setSearch}
              placeholder={placeholder}
              disabled={isDisabled}
            />
            <CommandList>
              {props.loading ? (
                <CommandEmpty>Loading...</CommandEmpty>
              ) : filteredOptions.length === 0 && !canCreate ? (
                <CommandEmpty>No results found</CommandEmpty>
              ) : null}
              {selectedOption ? (
                <CommandGroup heading="Selection">
                  <CommandItem
                    value="__clear__"
                    onSelect={() => handleSelect(undefined)}
                  >
                    <X className="mr-2 size-4" />
                    Clear selection
                  </CommandItem>
                </CommandGroup>
              ) : null}
              <CommandGroup heading="Options">
                {filteredOptions.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    onSelect={() => handleSelect(option)}
                  >
                    <Check
                      className={cn(
                        "mr-2 size-4",
                        option.value === selectedOption?.value
                          ? "opacity-100"
                          : "opacity-0"
                      )}
                    />
                    {option.label}
                  </CommandItem>
                ))}
                {canCreate ? (
                  <CommandItem
                    value="__create__"
                    onSelect={() => handleCreate(search.trim())}
                  >
                    {isCreating ? (
                      <Loader2 className="mr-2 size-4 animate-spin" />
                    ) : (
                      <Plus className="mr-2 size-4" />
                    )}
                    Create &ldquo;{search.trim()}&rdquo;
                  </CommandItem>
                ) : null}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};
